import type { ActiveSharedTabletOperator } from './operatorCredentialStore.js';
import {
  SharedTabletOperatorAssertionSchema,
  type SharedTabletOperatorAssertion,
  type SyncEventInput,
  type StockCommandV1,
} from '@trackeat/shared';

type Listener = () => void;
const listeners = new Set<Listener>();
let activeOperator: ActiveSharedTabletOperator | null = null;
let sharedTabletMode = false;

export function setActiveSharedTabletOperator(
  operator: ActiveSharedTabletOperator | null,
): void {
  activeOperator = operator;
  for (const listener of listeners) listener();
}

export function getActiveSharedTabletOperator(): ActiveSharedTabletOperator | null {
  return activeOperator;
}

export function setSharedTabletModeActive(active: boolean): void {
  sharedTabletMode = active;
  if (!active) activeOperator = null;
  for (const listener of listeners) listener();
}

export function getActiveOperatorAssertion(): SharedTabletOperatorAssertion | null {
  if (!activeOperator) return null;
  return {
    grant_id: activeOperator.grantId,
    grant_token: activeOperator.grantToken,
    tenant_personnel_id: activeOperator.tenantPersonnelId,
  };
}

export function getActiveOperatorRequestHeaders(): Record<string, string> {
  if (!activeOperator) return {};
  return {
    'x-trackeat-operator-grant-id': activeOperator.grantId,
    'x-trackeat-operator-grant-token': activeOperator.grantToken,
    'x-trackeat-operator-personnel-id': activeOperator.tenantPersonnelId,
    'x-trackeat-device-id': activeOperator.deviceId,
  };
}

export function requireActiveOperatorAssertion(): SharedTabletOperatorAssertion | undefined {
  const assertion = getActiveOperatorAssertion();
  if (sharedTabletMode && !assertion) {
    throw new Error('shared_tablet_operator_required');
  }
  return assertion ?? undefined;
}

export function attachActiveOperatorAssertion<T extends object>(value: T): T & {
  operator_assertion?: SharedTabletOperatorAssertion;
} {
  const assertion = requireActiveOperatorAssertion();
  return assertion ? { ...value, operator_assertion: assertion } : value;
}

export function splitOperatorAssertion<T extends object>(value: T): {
  assertion?: SharedTabletOperatorAssertion;
  value: Omit<T, 'operator_assertion'>;
} {
  const record = value as T & { operator_assertion?: unknown };
  const { operator_assertion: rawAssertion, ...withoutAssertion } = record;
  if (rawAssertion === undefined) return { value: withoutAssertion };
  return {
    assertion: SharedTabletOperatorAssertionSchema.parse(rawAssertion),
    value: withoutAssertion,
  };
}

export function decorateSyncEventForOperator(event: SyncEventInput): SyncEventInput {
  const payload = activeOperator && Object.prototype.hasOwnProperty.call(
    event.payload,
    'operator_name',
  )
    ? { ...event.payload, operator_name: activeOperator.fullName }
    : event.payload;
  return attachActiveOperatorAssertion({ ...event, payload });
}

/** Bind command metadata before materializing/signing its events. Never use on a replay. */
export function stockCommandForActiveOperator(command: StockCommandV1): StockCommandV1 {
  if (!activeOperator) return command;
  const operator_name = activeOperator.fullName;
  if (command.movement_class === 'production') {
    return {
      ...command,
      inputs: command.inputs.map((line) => ({ ...line, operator_name })),
      outputs: command.outputs.map((line) => ({ ...line, operator_name })),
    };
  }
  if ('lines' in command) {
    return { ...command, lines: command.lines.map((line) => ({ ...line, operator_name })) } as StockCommandV1;
  }
  return command;
}

export function optimisticOperatorPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  if (!activeOperator) return payload;
  return {
    ...payload,
    _operator: {
      tenant_personnel_id: activeOperator.tenantPersonnelId,
      full_name: activeOperator.fullName,
      role: activeOperator.role,
      identity_fingerprint: activeOperator.identityFingerprint,
      scheme: 'trackeat-shared-tablet-operator-v1',
      grant_id: activeOperator.grantId,
    },
  };
}

export function subscribeActiveSharedTabletOperator(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
